import { useState } from 'react';
import { AlertTriangle, Clock, UserCheck, Calendar, ExternalLink, ChevronRight, ChevronDown, Trash2, ClipboardCheck } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { DeliverableStatusBadge } from './DeliverableStatusBadge';
import { DeliverableEditFields, ALL_STATUSES, STATUS_LABELS } from './DeliverablesSection';
import { DeliverableFileList } from './DeliverableFileList';
import { DeliverableDocumentList } from './DeliverableDocumentList';
import { DeliverableScopePortalPrompt } from './DeliverableScopePortalPrompt';
import { getBallHolder, isStale, isOverdue, timeAgo } from './deliverable-utils';
import { useProjectScopes } from '@/hooks/useProjectScopes';
import { useDeliverable } from '@/hooks/useDeliverables';
import { useDeliverableScopePortal } from '@/hooks/useDeliverableScopePortal';
import { ShareWithClientDialog } from '@/components/projects/ShareWithClientDialog';
import type { Deliverable, DeliverableStatus } from '@/hooks/useDeliverables';
import type { TeamMember } from '@/hooks/useTeamMembers';
import type { ScopeContact } from '@/components/projects/ShareWithClientDialog';

export interface DeliverableRowProps {
  deliverable: Deliverable & { project_name?: string };
  expandedId: string | null;
  onToggle: () => void;
  onStatusChange: (id: string, status: DeliverableStatus) => void;
  onDelete: (id: string) => void;
  onNavigate: (path: string) => void;
  teamMembers: TeamMember[] | undefined;
  onUpdate: (id: string, data: any) => void;
  compact: boolean;
  showProject?: boolean;
}

export function DeliverableRow({
  deliverable: d,
  expandedId,
  onToggle,
  onStatusChange,
  onDelete,
  onNavigate,
  teamMembers,
  onUpdate,
  compact,
  showProject,
}: DeliverableRowProps) {
  const ballHolder = getBallHolder(d);
  const stale = isStale(d);
  const overdue = isOverdue(d);
  const isExpanded = expandedId === d.id;

  // State for portal dialog
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareDialogContacts, setShareDialogContacts] = useState<ScopeContact[]>([]);

  // Lazily fetch project scopes when row is expanded (for scope dropdown in edit fields)
  const { data: scopesData } = useProjectScopes(isExpanded ? d.project_id : undefined);
  const scopes = scopesData?.scopes;

  // Fetch full deliverable (with linked_site_visits) when expanded
  const { data: fullDeliverable, isLoading: isLoadingDeliverable } = useDeliverable(isExpanded ? d.id : undefined);

  // Get scope for this deliverable if expanded
  const deliverableScope = isExpanded && d.scope_id
    ? scopes?.find(s => s.id === d.scope_id)
    : undefined;

  // Check portal status for this scope
  const { isExternal, portalExists, orgName, isLoading: isLoadingPortal } = useDeliverableScopePortal(
    isExpanded ? d.project_id : undefined,
    deliverableScope
  );

  const handleOpenShareDialog = (scopeContacts?: ScopeContact[]) => {
    setShareDialogContacts(scopeContacts || []);
    setShowShareDialog(true);
  };

  const handleShareDialogClose = () => {
    setShowShareDialog(false);
  };

  return (
    <div
      className={`rounded-md border overflow-hidden ${
        stale ? 'border-red-300' : overdue ? 'border-amber-200' : ''
      }`}
    >
      <div
        className={`flex items-center gap-2 py-1.5 px-3 cursor-pointer transition-colors group min-w-0 ${
          stale
            ? 'bg-red-50/50 hover:bg-red-50'
            : overdue
              ? 'bg-amber-50/30 hover:bg-amber-50/50'
              : 'bg-card hover:bg-accent/50'
        }`}
        onClick={onToggle}
      >
        {isExpanded
          ? <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
          : <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
        }

        <Select
          value={d.status}
          onValueChange={(value) => onStatusChange(d.id, value as DeliverableStatus)}
        >
          <SelectTrigger
            className="w-[120px] h-6 text-xs border-0 p-0 focus:ring-0"
            onClick={(e) => e.stopPropagation()}
          >
            <DeliverableStatusBadge status={d.status} />
          </SelectTrigger>
          <SelectContent>
            {ALL_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                <DeliverableStatusBadge status={s} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="text-sm font-medium truncate flex-1 min-w-0" title={d.title}>{d.title}</span>

        {/* Project name — shown in cross-project views */}
        {showProject && d.project_name && (
          <span className="hidden sm:inline text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground border whitespace-nowrap">
            {d.project_name}
          </span>
        )}

        {/* Scope badge */}
        {d.scope_name && (
          <span className="hidden sm:inline text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 border border-violet-200 whitespace-nowrap">
            {d.scope_name}
          </span>
        )}

        {/* Time in status */}
        {d.status_changed_at && d.status !== 'pending' && (
          <span
            className={`flex items-center gap-1 text-xs whitespace-nowrap ${
              stale ? 'text-red-600 font-medium' : 'text-muted-foreground'
            }`}
            title={`${STATUS_LABELS[d.status as DeliverableStatus]} since ${new Date(d.status_changed_at).toLocaleDateString()}`}
          >
            {stale && <AlertTriangle className="h-3 w-3" />}
            <Clock className="h-3 w-3" />
            {timeAgo(d.status_changed_at)}
          </span>
        )}

        {/* Ball holder */}
        {ballHolder && (
          <span className="hidden sm:flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full border border-blue-200 whitespace-nowrap">
            <UserCheck className="h-3 w-3" />
            {ballHolder.name}
          </span>
        )}

        {/* Due date */}
        {d.due_date && (
          <span className={`hidden sm:flex items-center gap-1 text-xs whitespace-nowrap ${
            overdue ? 'text-red-600 font-medium' : 'text-muted-foreground'
          }`}>
            <Calendar className="h-3 w-3" />
            {new Date(d.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        )}

        {/* Open in project — only when not inside a project group */}
        {!compact && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            onClick={(e) => { e.stopPropagation(); onNavigate(`/projects/${d.project_id}?section=deliverables`); }}
            title="Open in project"
          >
            <ExternalLink className="h-3 w-3 text-muted-foreground" />
          </Button>
        )}

        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
          onClick={(e) => { e.stopPropagation(); onDelete(d.id); }}
          title="Delete deliverable"
        >
          <Trash2 className="h-3 w-3 text-destructive" />
        </Button>
      </div>

      {isExpanded && (
        <>
          <DeliverableEditFields
            deliverable={d}
            teamMembers={teamMembers}
            scopes={scopes}
            onUpdate={onUpdate}
          />

          {/* Portal prompt for external scopes */}
          {deliverableScope && (
            <DeliverableScopePortalPrompt
              deliverableId={d.id}
              scope={deliverableScope}
              portalExists={portalExists}
              orgName={orgName}
              projectId={d.project_id}
              onOpenDialog={handleOpenShareDialog}
              isLoading={isLoadingPortal}
            />
          )}

          <DeliverableFileList deliverableId={d.id} />
          <DeliverableDocumentList deliverableId={d.id} />

          {/* Field Reviews */}
          <div className="border-t px-3 py-2.5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Field Reviews</span>
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-xs gap-1"
                onClick={() => onNavigate(
                  `/projects/${d.project_id}/site-visits?deliverable_id=${d.id}&title=${encodeURIComponent(d.title)}&visit_type=site_review`
                )}
              >
                <ClipboardCheck className="h-3 w-3" />
                Create Field Review
              </Button>
            </div>
            {isLoadingDeliverable ? (
              <p className="text-xs text-muted-foreground">Loading...</p>
            ) : fullDeliverable?.linked_site_visits && fullDeliverable.linked_site_visits.length > 0 ? (
              <div className="space-y-1">
                {fullDeliverable.linked_site_visits.map((sv) => (
                  <div
                    key={sv.id}
                    className="flex items-center gap-2 text-xs cursor-pointer hover:bg-accent/50 rounded px-1 py-0.5"
                    onClick={() => onNavigate(`/projects/${d.project_id}/site-visits`)}
                  >
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      sv.status === 'submitted' ? 'bg-purple-100 text-purple-700' :
                      sv.status === 'completed' ? 'bg-green-100 text-green-700' :
                      sv.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{sv.status}</span>
                    <span className="flex-1 truncate">{sv.title}</span>
                    <span className="text-muted-foreground whitespace-nowrap">
                      {new Date(sv.visit_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No field reviews linked yet.</p>
            )}
          </div>
        </>
      )}

      {/* Share with Client dialog */}
      <ShareWithClientDialog
        open={showShareDialog}
        onOpenChange={handleShareDialogClose}
        projectId={d.project_id}
        projectName={d.project_name || ''}
        scopeContacts={shareDialogContacts}
      />
    </div>
  );
}
